import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import payrollService from '../services/payrollService';
import { useNotification } from '../context/NotificationContext';
import ConfirmActionModal from '../components/ConfirmActionModal';
import { formatDate } from '../utils/datetime';
import '../styles/general.css';

// Mirrors the backend's EDITABLE_STATUSES; the API rejects writes to the rest.
const EDITABLE_STATUSES = ['borrador', 'aprobado'];

const PayrollDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { notify } = useNotification();

  const [payroll, setPayroll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingAction, setPendingAction] = useState(null);
  const [processing, setProcessing] = useState(false);

  const loadPayroll = useCallback(async () => {
    try {
      const res = await payrollService.getPayrollById(id);
      setPayroll(res.data);
    } catch (err) {
      console.error('Error loading payroll:', err);
      notify.error(t('payroll.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [id, notify, t]);

  useEffect(() => {
    loadPayroll();
  }, [loadPayroll]);

  const handleExport = async () => {
    try {
      await payrollService.exportReceipt(id);
    } catch (err) {
      console.error('Error exporting payroll receipt:', err);
      notify.error(t('common.failedToLoad'));
    }
  };

  const handleActionConfirm = async () => {
    if (!pendingAction) return;
    setProcessing(true);
    try {
      await payrollService[pendingAction](id);
      notify.success(t('common.updatedSuccessfully'));
      setPendingAction(null);
      loadPayroll();
    } catch (err) {
      console.error(`Error running ${pendingAction} on payroll:`, err);
      notify.error(err.response?.data?.detail || t('common.failedToUpdate'));
    } finally {
      setProcessing(false);
    }
  };

  if (loading || !payroll) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  const row = (labelKey, value) => (
    <div className="form-group">
      <span className="form-label">{t(labelKey)}</span>
      <div className="section-value">{value}</div>
    </div>
  );

  return (
    <div className="page-container-wide">
      <div className="page-header">
        <h1>{payroll.employee_name}</h1>
        {/* Period and dates on the right, per the spec. All three are
            DateField values, so formatDate does not convert them. */}
        <div className="order-number-badge" data-testid="payroll-period">
          <span className="order-number-label">{t('payroll.period')}</span>
          <span className="order-number-value">{payroll.period_code}</span>
          <span className="order-number-label">
            {formatDate(payroll.period_start)} / {formatDate(payroll.period_end)}
          </span>
          <span className="order-number-label">
            {t('payroll.paymentDate')}: {formatDate(payroll.payment_date)}
          </span>
        </div>
      </div>

      <div className="grid-3">
        {row('payroll.frequency', t(`payroll.frequencies.${payroll.frequency}`))}
        {row('common.status', t(`payroll.status.${payroll.status}`))}
        {row('payroll.paymentReference', payroll.payment_reference || '—')}
        {row('payroll.baseSalary', payroll.base_salary)}
        {row('payroll.extraPay', payroll.extra_pay)}
        {row(
          'payroll.bonus',
          `${payroll.bonus}${payroll.bonus_description ? ` (${payroll.bonus_description})` : ''}`
        )}
        {row(
          'payroll.bonus2',
          `${payroll.bonus2}${payroll.bonus2_description ? ` (${payroll.bonus2_description})` : ''}`
        )}
        {row(
          'payroll.deduction',
          `${payroll.deduction}${payroll.deduction_description ? ` (${payroll.deduction_description})` : ''}`
        )}
        {row('payroll.socialSecurity', payroll.social_security)}
        {row('payroll.savings', payroll.savings)}
        {row('payroll.bankName', payroll.bank_name || '—')}
        {row('payroll.bankAccountNumber', payroll.bank_account_number || '—')}
      </div>

      <div className="totals-footer">
        <div className="totals-row">
          <span>{t('payroll.totalEarnings')}</span>
          <span data-testid="total-earnings">{payroll.total_earnings}</span>
        </div>
        <div className="totals-row">
          <span>{t('payroll.totalDeductions')}</span>
          <span data-testid="total-deductions">{payroll.total_deductions}</span>
        </div>
        <div className="totals-row grand">
          <span>{t('payroll.totalPayable')}</span>
          <span data-testid="total-payable">{payroll.total_payable}</span>
        </div>
      </div>

      <div className="flex-center gap-15 mt-20">
        <button onClick={() => navigate('/payroll')} className="btn btn-secondary">
          {t('payroll.backToList')}
        </button>
        <button onClick={handleExport} className="btn btn-export">
          {t('payroll.exportReceipt')}
        </button>
        {/* Editable in draft and approved. Approved is where the bank and
            payment reference usually get filled in - the backend only
            requires them at the transition to paid. */}
        {EDITABLE_STATUSES.includes(payroll.status) && (
          <button
            onClick={() => navigate(`/payroll/${payroll.id}/edit`)}
            className="btn btn-primary"
          >
            {t('common.edit')}
          </button>
        )}
        {payroll.status === 'borrador' && (
          <button onClick={() => setPendingAction('approve')} className="btn btn-success">
            {t('payroll.approve')}
          </button>
        )}
        {payroll.status === 'aprobado' && (
          <button onClick={() => setPendingAction('pay')} className="btn btn-success">
            {t('payroll.pay')}
          </button>
        )}
      </div>

      <ConfirmActionModal
        isOpen={!!pendingAction}
        onClose={() => setPendingAction(null)}
        onConfirm={handleActionConfirm}
        title={t('payroll.title')}
        message={pendingAction === 'approve'
          ? t('payroll.confirmApprove')
          : t('payroll.confirmPay')}
        confirmLabel={t('common.confirm')}
        confirming={processing}
      />
    </div>
  );
};

export default PayrollDetailPage;
